import { diffWords } from 'diff';
import type { HistorialFullEntry } from '@/hooks/useLatestHistorialMap';
import { Modal, SearchableSelect } from '@/components/ui';
import { CorreccionesChat } from '@/components/asesorias/CorreccionesChat';
import { CalificacionBadge } from '@/components/revision/CalificacionBadge';
import type { RevisionEntry } from '@/utils/revisionHistorial';
import type { CalificacionType } from '@/types/types';
import { CheckCircle, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

type VistaTexto = 'diff' | 'lado_a_lado';

const VISTA_TEXTO_OPCIONES = [
  { value: 'diff', label: 'Ver diferencia' },
  { value: 'lado_a_lado', label: 'Antes vs después' },
];

// ---------------------------------------------------------------------------
// Word-level diff (antes → después), full text, no truncation.
// ---------------------------------------------------------------------------

function WordDiff({ prev, current }: { prev: string; current: string }) {
  const parts = diffWords(prev, current);
  return (
    <p className="text-[13px] leading-relaxed wrap-break-word whitespace-pre-wrap font-medium">
      {parts.map((part, i) =>
        part.removed ? (
          <span key={i} className="bg-red-100 text-red-700 line-through decoration-red-400 rounded-sm px-0.5">
            {part.value}
          </span>
        ) : part.added ? (
          <span key={i} className="bg-green-100 text-green-800 font-bold rounded-sm px-0.5">
            {part.value}
          </span>
        ) : (
          <span key={i} className="text-gray-700">
            {part.value}
          </span>
        )
      )}
    </p>
  );
}

// ---------------------------------------------------------------------------
// RevisionFieldModal
// ---------------------------------------------------------------------------

export interface RevisionFieldModalProps {
  open: boolean;
  onClose: () => void;
  titulo: string;
  kind: 'terna' | 'requisito';
  textoActual?: string | null;
  textoAnterior?: string | null;
  /** Numeral y capítulo del requisito — solo aplica a kind="requisito". */
  numeral?: string;
  capitulo?: string;
  /** Calificación vigente / anterior del requisito (C/NC/CP/NA) — solo aplica a kind="requisito". */
  calActual?: CalificacionType | null;
  calAnterior?: CalificacionType | null;
  historialList?: HistorialFullEntry[];
  estadoActual?: 'APROBADO' | 'RECHAZADO' | 'COMENTADO';
  observacionActual?: string;
  onAprobar?: (observacion?: string) => Promise<void>;
  onRechazar?: (observacion?: string) => Promise<void>;
}

export function RevisionFieldModal({
  open,
  onClose,
  titulo,
  kind,
  textoActual,
  textoAnterior,
  numeral,
  capitulo,
  calActual,
  calAnterior,
  historialList,
  estadoActual,
  observacionActual,
  onAprobar,
  onRechazar,
}: RevisionFieldModalProps) {
  const [comentario, setComentario] = useState('');
  const [guardando, setGuardando] = useState<'aprobar' | 'rechazar' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vistaTexto, setVistaTexto] = useState<VistaTexto>('diff');

  // Prefill the textarea whenever the modal opens for a new element.
  useEffect(() => {
    if (open) {
      setComentario('');
      setError(null);
      setVistaTexto('diff');
    }
  }, [open]);

  const hasPrev = !!textoAnterior && textoAnterior !== '' && textoAnterior !== textoActual;

  const handleAprobar = async () => {
    setGuardando('aprobar');
    setError(null);
    try {
      await onAprobar?.(comentario.trim() || undefined);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setGuardando(null);
    }
  };
  const handleRechazar = async () => {
    setGuardando('rechazar');
    setError(null);
    try {
      await onRechazar?.(comentario.trim() || undefined);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setGuardando(null);
    }
  };

  const footer = (
    <div className="space-y-2">
      {error && <p className="text-[12px] text-red-600">{error}</p>}
      {/* Actions — aprobar o rechazar, con observación opcional. Se guarda de inmediato. */}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={guardando !== null}
          className="px-3 py-1.5 text-[12px] font-semibold bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          Cerrar
        </button>

        {onRechazar && (
          <button
            type="button"
            onClick={handleRechazar}
            disabled={guardando !== null}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg border transition-colors disabled:opacity-50 ${
              estadoActual === 'RECHAZADO'
                ? 'bg-[#fdf0ee] text-[#c0392b] border-[#f5c6c0]'
                : 'bg-white text-[#c0392b] border-[#f5c6c0] hover:bg-[#fdf0ee]'
            }`}
          >
            <XCircle className="w-4 h-4" />
            {guardando === 'rechazar' ? 'Guardando...' : 'Rechazar'}
          </button>
        )}

        {onAprobar && (
          <button
            type="button"
            onClick={handleAprobar}
            disabled={guardando !== null}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg border transition-colors disabled:opacity-50 ${
              estadoActual === 'APROBADO'
                ? 'bg-[#eaf5ee] text-[#2d7a4f] border-[#b7dfc7]'
                : 'bg-white text-[#2d7a4f] border-[#b7dfc7] hover:bg-[#eaf5ee]'
            }`}
          >
            <CheckCircle className="w-4 h-4" />
            {guardando === 'aprobar' ? 'Guardando...' : 'Aprobar'}
          </button>
        )}
      </div>
    </div>
  );

  const isDateTitle = titulo.toLowerCase().includes('fecha');

  return (
    <Modal isOpen={open} onClose={onClose} title={titulo} size="lg" footer={footer}>
      <div className="space-y-4">
        {/* Full text — antes vs después / diff */}
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Contenido</div>
            {hasPrev && !isDateTitle && (
              <SearchableSelect
                value={vistaTexto}
                onChange={(v) => setVistaTexto(v as VistaTexto)}
                options={VISTA_TEXTO_OPCIONES}
                className="w-40"
              />
            )}
          </div>
          {kind === 'requisito' && (calActual || calAnterior) && <CalificacionBadge cal={calActual ?? null} prev={calAnterior ?? null} />}
          {hasPrev ? (
            vistaTexto === 'diff' && !isDateTitle ? (
              <WordDiff prev={textoAnterior!} current={textoActual ?? ''} />
            ) : (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-2.5">
                  <div className="text-[9px] font-bold uppercase tracking-wide text-gray-400 mb-1">Antes</div>
                  <p className="text-[12px] leading-relaxed text-gray-500 wrap-break-word whitespace-pre-wrap italic">{textoAnterior}</p>
                </div>
                <div className="rounded-lg border border-blue-100/60 bg-blue-50/30 p-2.5">
                  <div className="text-[9px] font-bold uppercase tracking-wide text-blue-500 mb-1">Después</div>
                  <p className="text-[12px] leading-relaxed text-blue-900 font-medium wrap-break-word whitespace-pre-wrap">
                    {textoActual || <span className="text-gray-300 italic">No registrado</span>}
                  </p>
                </div>
              </div>
            )
          ) : kind === 'requisito' ? (
            <div className="space-y-1">
              {numeral && <div className="text-[15px] font-bold text-gray-900">{numeral}</div>}
              {capitulo && <div className="text-[11px] font-medium text-gray-500">{capitulo}</div>}
              <p className="text-[13px] font-normal leading-relaxed text-gray-700 wrap-break-word whitespace-pre-wrap">
                {textoActual || <span className="text-gray-300 italic">No registrado</span>}
              </p>
            </div>
          ) : (
            <p className="text-[13px] leading-relaxed text-gray-700 wrap-break-word whitespace-pre-wrap">
              {textoActual || <span className="text-gray-300 italic">No registrado</span>}
            </p>
          )}
        </div>

        {/* Conversación completa (chat), misma vista que el asesor, solo lectura para el admin */}
        <CorreccionesChat entries={(historialList ?? []) as unknown as RevisionEntry[]} actorActual="admin" canReply={false} />

        {/* Comment textarea — opcional, se adjunta al veredicto */}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5 block">Observación (opcional)</label>
          <textarea
            autoFocus
            rows={4}
            className="w-full px-3 py-2 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white resize-none text-gray-800"
            placeholder="Comentario opcional para el asesor..."
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
